import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, fmtBRL, CATEGORIA_LABEL, CATEGORIA_OPTS, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, Badge } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ScanLine, Camera, Plus, ArrowDownToLine, ArrowUpFromLine, Search, X } from "lucide-react";

const emptyProduct = { codigo: "", codigo_barras: "", nome: "", categoria: "Outro", unidade: "UN", preco_custo: 0, estoque_atual: 0, estoque_minimo: 0, descricao: "" };

export default function Scan() {
  const { hasRole, user } = useAuth();
  const canEdit = hasRole("almoxarife");
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const [code, setCode] = useState("");
  const [product, setProduct] = useState(null);
  const [matches, setMatches] = useState([]);     // múltiplos resultados quando busca por nome
  const [notFound, setNotFound] = useState(false);
  const [history, setHistory] = useState([]);
  const [camOpen, setCamOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [movOpen, setMovOpen] = useState(false);
  const [movTipo, setMovTipo] = useState("entrada");
  const [movQty, setMovQty] = useState(1);
  const [movMotivo, setMovMotivo] = useState("");
  const [movResp, setMovResp] = useState("");
  const [newProd, setNewProd] = useState(emptyProduct);

  useEffect(() => {
    inputRef.current?.focus();
    const handler = () => inputRef.current?.focus();
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    if (user?.name && !movResp) setMovResp(user.name);
  }, [user, movResp]);

  const selectProduct = (p) => {
    setProduct(p);
    setMatches([]);
    setNotFound(false);
    setHistory((h) => [{ time: new Date().toLocaleTimeString("pt-BR"), code: p.codigo, found: true, name: p.nome }, ...h].slice(0, 10));
  };

  const lookup = async (scanned) => {
    const c = (scanned || code).trim();
    if (!c) return;
    setNotFound(false); setProduct(null); setMatches([]);
    try {
      const { data } = await api.get(`/products/search/lookup`, { params: { q: c } });
      if (Array.isArray(data) && data.length === 1) {
        selectProduct(data[0]);
        setCode("");
      } else if (Array.isArray(data) && data.length > 1) {
        setMatches(data);
        setHistory((h) => [{ time: new Date().toLocaleTimeString("pt-BR"), code: c, found: true, name: `${data.length} resultados` }, ...h].slice(0, 10));
      } else {
        // 0 resultados
        setNotFound(true);
        setNewProd({ ...emptyProduct, codigo_barras: c, codigo: c, nome: c });
        setHistory((h) => [{ time: new Date().toLocaleTimeString("pt-BR"), code: c, found: false }, ...h].slice(0, 10));
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const submitMov = async (e) => {
    e.preventDefault();
    try {
      const motivo = [movMotivo, movResp && `Responsável: ${movResp}`].filter(Boolean).join(" — ");
      await api.post("/movements", { product_id: product.id, tipo: movTipo, quantidade: parseFloat(movQty), motivo });
      toast.success(`${movTipo === "entrada" ? "Entrada" : "Saída"} registrada`);
      setMovOpen(false); setMovQty(1); setMovMotivo("");
      const { data } = await api.get(`/products/${product.id}`);
      setProduct(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newProd,
        preco_custo: parseFloat(newProd.preco_custo) || 0,
        estoque_atual: parseFloat(newProd.estoque_atual) || 0,
        estoque_minimo: parseFloat(newProd.estoque_minimo) || 0,
      };
      const { data } = await api.post("/products", payload);
      toast.success("Produto cadastrado");
      setNewOpen(false); setNotFound(false);
      selectProduct(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const startCam = async () => {
    setCamOpen(true);
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("scanner-target");
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 } },
          (decoded) => { stopCam(); lookup(decoded); },
          () => {}
        );
      } catch (e) {
        toast.error("Não foi possível acessar câmera: " + e.message);
        setCamOpen(false);
      }
    }, 200);
  };

  const stopCam = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } }
    catch { /* ignore */ }
    setCamOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="scan-page">
      <SectionTitle action={
        <Btn variant="dark" onClick={startCam} data-testid="open-camera-btn"><Camera className="w-4 h-4" />Usar câmera</Btn>
      }>
        Bipar · <ScanLine className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      {/* Scanner / busca input */}
      <div className="brutal-card p-6">
        <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">Leitor de código de barras · ou digite o nome</div>
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-[#555]" />
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
            placeholder='Aponte o leitor, digite o código OU descrição (ex.: "café") + Enter'
            className="flex-1 border-2 border-[#111] p-3 text-lg font-mono-data outline-none focus:border-[#FF4F00] bg-white"
            data-testid="scan-input"
            autoFocus
          />
          <Btn onClick={() => lookup()} data-testid="lookup-btn">Buscar</Btn>
        </div>
        <div className="mt-2 font-mono-data text-[10px] uppercase tracking-widest text-[#777]">
          Dica: leitores USB se comportam como teclado · pressione ENTER ao final
        </div>
      </div>

      {/* Múltiplos resultados — picker */}
      {matches.length > 0 && (
        <div className="brutal-card p-4" data-testid="scan-matches">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57] mb-3">
            {matches.length} produtos encontrados · selecione um
          </div>
          <div className="grid gap-2">
            {matches.map((m) => (
              <button
                key={m.id}
                onClick={() => selectProduct(m)}
                data-testid={`match-${m.id}`}
                className="brutal-card-sm p-3 text-left hover:bg-[#F0F7F2] flex items-center justify-between gap-3 transition"
              >
                <div className="min-w-0">
                  <div className="font-display font-bold uppercase tracking-tight">{m.nome}</div>
                  <div className="font-mono-data text-xs text-[#555]">{m.codigo}{m.codigo_barras ? ` · EAN ${m.codigo_barras}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <Badge variant="green">{CATEGORIA_LABEL[m.categoria] || m.categoria}</Badge>
                  <div className="font-mono-data text-xs mt-1">{m.estoque_atual} {m.unidade}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Produto selecionado */}
      {product && (
        <div className="brutal-card p-6" data-testid="scan-result">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <Badge variant="green">{CATEGORIA_LABEL[product.categoria]}</Badge>
              <h3 className="font-display font-black text-2xl uppercase tracking-tighter mt-2">{product.nome}</h3>
              <div className="font-mono-data text-sm text-[#555] mt-1">
                {product.codigo} {product.codigo_barras && <span className="ml-2">· EAN {product.codigo_barras}</span>}
              </div>
            </div>
            <button onClick={() => setProduct(null)} className="p-1.5 border-2 border-[#111] hover:bg-[#111] hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Estoque</div><div className="font-display font-black text-2xl">{product.estoque_atual} {product.unidade}</div></div>
            <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Mínimo</div><div className="font-display font-black text-2xl">{product.estoque_minimo}</div></div>
            <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Custo</div><div className="font-mono-data font-bold text-base">{fmtBRL(product.preco_custo)}</div></div>
            <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Categoria</div><div className="font-mono-data font-bold text-base">{CATEGORIA_LABEL[product.categoria] || product.categoria}</div></div>
          </div>

          {product.localizacao && <div className="font-mono-data text-xs text-[#555] mb-3">📍 {product.localizacao}</div>}

          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-3 border-t-2 border-[#111]">
              <Btn variant="success" onClick={() => { setMovTipo("entrada"); setMovOpen(true); }} data-testid="quick-entrada"><ArrowDownToLine className="w-4 h-4" />Entrada de Estoque</Btn>
              <Btn variant="primary" onClick={() => { setMovTipo("saida"); setMovOpen(true); }} data-testid="quick-saida"><ArrowUpFromLine className="w-4 h-4" />Saída de Estoque</Btn>
            </div>
          )}
        </div>
      )}

      {/* Nenhum resultado */}
      {notFound && canEdit && (
        <div className="brutal-card p-6 bg-[#FFF8E1]" data-testid="not-found-block">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#5C4400]">Não encontrado</div>
          <h3 className="font-display font-black text-xl uppercase mt-1 mb-3">Cadastrar novo produto?</h3>
          <p className="text-sm text-[#555] mb-4">Este código/descrição não está no sistema. Você pode cadastrá-lo agora.</p>
          <Btn variant="primary" onClick={() => setNewOpen(true)} data-testid="open-quick-new"><Plus className="w-4 h-4" />Cadastrar produto</Btn>
        </div>
      )}

      {/* Histórico */}
      {history.length > 0 && (
        <div className="brutal-card">
          <div className="bg-[#3D6B57] text-white px-5 py-2 font-display font-bold uppercase text-xs tracking-widest">Bipes recentes</div>
          <table className="brutal-table">
            <thead><tr><th>Hora</th><th>Termo</th><th>Resultado</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td className="font-mono-data text-xs">{h.time}</td>
                  <td className="font-mono-data text-xs">{h.code}</td>
                  <td>{h.name || "—"}</td>
                  <td>{h.found ? <Badge variant="success">Encontrado</Badge> : <Badge variant="warn">Não cadastrado</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Câmera */}
      {camOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={stopCam}>
          <div className="brutal-card p-4 w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black uppercase text-sm tracking-tight">Aponte para o código</h3>
              <button onClick={stopCam} className="text-[#111] hover:text-[#FF4F00]"><X className="w-5 h-5" /></button>
            </div>
            <div id="scanner-target" className="w-full bg-black" style={{ minHeight: 280 }}></div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#777] mt-3 text-center">Permita o acesso à câmera quando solicitado</div>
          </div>
        </div>
      )}

      {/* Movimentação rápida */}
      <Modal open={movOpen} onClose={() => setMovOpen(false)} title={`${movTipo === "entrada" ? "Entrada" : "Saída"} de Estoque · ${product?.nome || ""}`} size="md">
        <form onSubmit={submitMov} className="grid grid-cols-2 gap-4" data-testid="quick-mov-form">
          <Field label="Quantidade *" type="number" required value={movQty} onChange={setMovQty} testid="quick-mov-qty" />
          <Field label="Responsável" value={movResp} onChange={setMovResp} testid="quick-mov-resp" />
          <TextArea label="Observação / Motivo" value={movMotivo} onChange={setMovMotivo} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setMovOpen(false)}>Cancelar</Btn>
            <Btn type="submit" variant={movTipo === "entrada" ? "success" : "primary"} data-testid="quick-mov-save">Confirmar</Btn>
          </div>
        </form>
      </Modal>

      {/* Cadastro rápido */}
      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Cadastrar produto (rápido)" size="xl">
        <form onSubmit={submitNew} className="grid grid-cols-2 gap-4" data-testid="quick-new-form">
          <Field label="Código interno *" required value={newProd.codigo} onChange={(v) => setNewProd({ ...newProd, codigo: v })} />
          <Field label="Código de Barras (EAN)" value={newProd.codigo_barras} onChange={(v) => setNewProd({ ...newProd, codigo_barras: v })} />
          <Field label="Nome *" required value={newProd.nome} onChange={(v) => setNewProd({ ...newProd, nome: v })} colspan />
          <SelectField label="Categoria *" required value={newProd.categoria} onChange={(v) => setNewProd({ ...newProd, categoria: v })} options={CATEGORIA_OPTS.map((c) => ({ value: c, label: CATEGORIA_LABEL[c] }))} />
          <Field label="Unidade" value={newProd.unidade} onChange={(v) => setNewProd({ ...newProd, unidade: v })} />
          <Field label="Preço Custo" type="number" value={newProd.preco_custo} onChange={(v) => setNewProd({ ...newProd, preco_custo: v })} />
          <Field label="Estoque inicial" type="number" value={newProd.estoque_atual} onChange={(v) => setNewProd({ ...newProd, estoque_atual: v })} />
          <Field label="Estoque Mínimo" type="number" value={newProd.estoque_minimo} onChange={(v) => setNewProd({ ...newProd, estoque_minimo: v })} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setNewOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="quick-new-save">Cadastrar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
